import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import { FloatingFormItem, InputText } from '@/components/_common';
import CancellationCodeSelect from '@/components/_common/CancellationCodeSelect';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { useCancelInvoiceHub } from '@/core/domain/invoiceTransmissionHub/Invoice/Invoice.hooks';
import invoiceHandleError from '@/core/domain/invoiceTransmissionHub/invoice.error.transmit';
import { useCancelInvoiceNfs } from '@/core/domain/nfs/NfseController/NfseController.hooks';
import { useDefaultMinLengthFieldError, useDisclosure } from '@/hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Button, Col, Form, Modal, Row } from 'antd';

interface Props {
  type: InvoiceType;
}

const CancelInternalButton = ({ type }: Props) => {
  const [form] = Form.useForm<{
    justification: string;
    codeCancellation: string;
  }>();

  const [modalVisible, setModalVisible] = useDisclosure(false);

  const { invoiceId, isNfs, cancellationCode } = useInvoiceFormContext();

  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();

  const cancelInvoiceHub = useCancelInvoiceHub();
  const cancelInvoiceNfs = useCancelInvoiceNfs();

  const handleSubmit = async () => {
    try {
      const { justification, codeCancellation } = await form.validateFields();

      type === InvoiceType.NFS
        ? await cancelInvoiceNfs.mutateAsync({
            invoiceId: Number(invoiceId),
            reasonCancellation: justification,
            codeCancellation,
          })
        : await cancelInvoiceHub.mutateAsync({
            invoiceId: Number(invoiceId),
            justification,
            type,
          });

      setModalVisible.toggle();
    } catch (err) {
      invoiceHandleError(err, 'Erro ao cancelar nota fiscal.');
    }
  };

  const loading = cancelInvoiceHub.isLoading || cancelInvoiceNfs.isLoading;

  return (
    <>
      {loading && (
        <ModalLoadingOverlay
          messages={['Cancelando nota...', 'Validando dados da nota...']}
        />
      )}

      <Button
        danger
        block
        type="primary"
        onClick={() => setModalVisible.toggle()}
        disabled={
          !hasPermissions([Authorities.FISCAL_INVOICE_CANCEL]) ||
          cancelInvoiceHub.isLoading
        }
      >
        Cancelar
      </Button>

      <Modal
        title="Cancelamento Nota Fiscal"
        open={modalVisible}
        onOk={handleSubmit}
        cancelButtonProps={{
          disabled: cancelInvoiceHub.isLoading,
        }}
        closable={cancelInvoiceHub.isLoading}
        maskClosable={cancelInvoiceHub.isLoading}
        onCancel={() => setModalVisible.toggle()}
        confirmLoading={cancelInvoiceHub.isLoading}
      >
        <Form form={form}>
          <Row>
            {!!cancellationCode?.length && isNfs ? (
              <CancellationCodeSelect isInternal />
            ) : (
              <Col span={24}>
                <FloatingFormItem
                  label="Justificativa do cancelamento da nota fiscal"
                  rules={[defaultMinLenghtFieldError(15)]}
                  name="justification"
                >
                  <InputText />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CancelInternalButton;
